"use client";

/**
 * DeferredAnalytics mounts Vercel Analytics, SpeedInsights, and Google
 * Analytics only after the main thread is idle (requestIdleCallback) or
 * after a 4-second timeout fallback.
 *
 * Prevents analytics scripts from contributing to TBT during the LCP window.
 * The gtag/js script (157 KB) and JS chunk evaluation (18s+ main-thread work)
 * drove the 2812ms TBT and 2967ms LCP element render delay on 2026-05-11.
 */

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";

const Analytics = dynamic(
  () => import("@vercel/analytics/next").then((mod) => mod.Analytics),
  { ssr: false }
);

const SpeedInsights = dynamic(
  () => import("@vercel/speed-insights/next").then((mod) => mod.SpeedInsights),
  { ssr: false }
);

const GoogleAnalytics = dynamic(
  () => import("@next/third-parties/google").then((mod) => mod.GoogleAnalytics),
  { ssr: false }
);

export function DeferredAnalytics({ gaId }: { gaId: string }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if ("requestIdleCallback" in window) {
      const id = (window as Window & typeof globalThis).requestIdleCallback(
        () => setReady(true),
        { timeout: 4000 }
      );
      return () => (window as Window & typeof globalThis).cancelIdleCallback(id);
    } else {
      const timer = setTimeout(() => setReady(true), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!ready) return null;

  return (
    <>
      <Analytics />
      <SpeedInsights />
      {process.env.NODE_ENV === "production" && <GoogleAnalytics gaId={gaId} />}
    </>
  );
}
